import './demand-sidebar-row.css';

import { uiColor } from '@kerfjs/ui/css-values';
import { ListItem } from '@kerfjs/ui/list-item';
import { LucideIcon } from '@kerfjs/ui/lucide-icon';
import type { PopupMenuEntry } from '@kerfjs/ui/popup-menu';
import type { SafeHtml } from 'kerfjs';
import {
  ArchiveRestore,
  Ban,
  Copy,
  FileText,
  MessageCircleQuestionMark,
  Undo2,
  UserCheck,
  UserMinus,
  Users,
} from 'lucide';

import {
  canCancelDemand,
  canReopenDemand,
  canReturnDemand,
  managesDemands,
} from '../../../../shared/demand-transitions.js';
import type { DemandListItem } from '../../../data/api.js';
import { assignableMembers, me } from '../../../data/state.js';
import { canAssignDemands, canCreateDemands } from '../../../lib/context.js';
import { initialsAvatarUrl } from '../../../lib/initials-avatar.js';

/** What a staff member's row menu needs to assign the demand (FR-5.20, HS2-C63T9N). */
export interface DemandRowAssign {
  /** The demand's assignee when the menu opened: the compare-and-set's expected value. */
  current: string | null;
  /** The signed-in member, for Assign to Me. */
  viewer: string | null;
  /** The staff members the Assign To submenu offers. */
  members: readonly { id: string; name: string }[];
}

/** The cancel/reopen actions a demand row offers this member (HS2-EY3F2G, shared server rules). */
export interface DemandRowActions {
  cancel: boolean;
  reopen: boolean;
  /** Start a new draft from this demand (HS2-Q2NPBT): anyone who may create demands. */
  duplicate: boolean;
  /** Send it back to its requester without rejecting it (HS2-T90PS1): reviewers and admins. */
  returnToRequester: boolean;
  /** Assign to Me / Assign To / Unassign (FR-5.20): staff only, else null. */
  assign: DemandRowAssign | null;
}

/** Which row actions the current member may use on a listed demand. */
export function demandRowActions(
  demand:
    | (Pick<DemandListItem, 'status' | 'cancelledFrom'> &
        Partial<Pick<DemandListItem, 'requesterName' | 'assigneeMemberId'>>)
    | undefined,
): DemandRowActions {
  if (demand === undefined)
    return { cancel: false, reopen: false, duplicate: false, returnToRequester: false, assign: null };
  const manager = managesDemands(me.value?.roles ?? []);
  // Procurement members cannot edit or create demands; requesters only see their own.
  const editor = canCreateDemands();
  return {
    cancel: editor && canCancelDemand(demand.status, manager),
    reopen: editor && canReopenDemand(demand.status, demand.cancelledFrom, manager),
    duplicate: editor,
    returnToRequester: manager && canReturnDemand(demand.status, (demand.requesterName ?? null) !== null),
    assign: canAssignDemands()
      ? {
          current: demand.assigneeMemberId ?? null,
          viewer: me.value?.memberId ?? null,
          members: assignableMembers.value,
        }
      : null,
  };
}

/**
 * Entries of a demand row's context menu, opened at the pointer by right-click (HS2-3BVPEF).
 * Cancel and Reopen appear only when this member may use them (HS2-EY3F2G).
 */
export function demandRowMenuEntries(demandId: string, actions: DemandRowActions): PopupMenuEntry[] {
  const entries: PopupMenuEntry[] = [
    {
      label: 'Open',
      icon: <LucideIcon icon={FileText} name="" />,
      action: 'demand-open-nav',
      attributes: { 'data-demand-id': demandId },
    },
  ];
  entries.push(...assignMenuEntries(demandId, actions.assign));
  if (actions.returnToRequester)
    entries.push({
      label: 'Return to Requester…',
      icon: <LucideIcon icon={Undo2} name="" />,
      action: 'demand-return-nav',
      attributes: { 'data-demand-id': demandId },
    });
  if (actions.duplicate)
    entries.push({
      label: 'Duplicate',
      icon: <LucideIcon icon={Copy} name="" />,
      action: 'demand-duplicate-nav',
      attributes: { 'data-demand-id': demandId },
    });
  if (actions.reopen)
    entries.push({
      label: 'Reopen',
      icon: <LucideIcon icon={ArchiveRestore} name="" />,
      action: 'demand-reopen-nav',
      attributes: { 'data-demand-id': demandId },
    });
  if (actions.cancel)
    entries.push({
      label: 'Cancel',
      icon: <LucideIcon icon={Ban} name="" />,
      action: 'demand-cancel-nav',
      attributes: { 'data-demand-id': demandId },
    });
  return entries;
}

/**
 * The row menu's assignment commands (FR-5.20, HS2-C63T9N): Assign to Me unless the viewer already
 * holds it, an Assign To submenu of staff with the current assignee checked, and Unassign while
 * assigned. Each carries the assignee it saw, so the server can refuse a concurrent change.
 */
function assignMenuEntries(demandId: string, assign: DemandRowAssign | null): PopupMenuEntry[] {
  if (assign === null) return [];
  const attributes = (assigneeId: string | null) => ({
    'data-demand-id': demandId,
    'data-assignee-id': assigneeId ?? '',
    'data-expected-assignee-id': assign.current ?? '',
  });
  const entries: PopupMenuEntry[] = [];
  if (assign.viewer !== null && assign.current !== assign.viewer)
    entries.push({
      label: 'Assign to Me',
      icon: <LucideIcon icon={UserCheck} name="" />,
      action: 'demand-assign-nav',
      attributes: attributes(assign.viewer),
    });
  if (assign.members.length > 0)
    entries.push({
      label: 'Assign To',
      icon: <LucideIcon icon={Users} name="" />,
      submenu: assign.members.map((member) => ({
        label: member.name,
        checked: member.id === assign.current,
        action: 'demand-assign-nav',
        attributes: attributes(member.id),
      })),
    });
  if (assign.current !== null)
    entries.push({
      label: 'Unassign',
      icon: <LucideIcon icon={UserMinus} name="" />,
      action: 'demand-assign-nav',
      attributes: attributes(null),
    });
  return entries;
}

/** The assignee's initials marker on a row (FR-5.20), named for assistive tech. */
function AssigneeMarker({ name }: { name: string }): SafeHtml {
  const label = `Assigned to ${name}`;
  return <img class="demand-nav-assignee" src={initialsAvatarUrl(name)} alt={label} title={label} />;
}

export function DemandSidebarRow({
  demand,
  activeId,
}: {
  demand: DemandListItem;
  activeId: string | undefined;
}): SafeHtml {
  const questionText = `${String(demand.openQuestionCount)} question${demand.openQuestionCount === 1 ? '' : 's'} waiting for an answer`;
  const questions =
    demand.openQuestionCount > 0 ? (
      <span class="demand-nav-questions" title={questionText}>
        <LucideIcon icon={MessageCircleQuestionMark} name="Questions Waiting" color={uiColor('text-quiet')} />
      </span>
    ) : null;
  const assignee = demand.assigneeName;
  const trailing =
    questions === null && assignee === null ? undefined : (
      <span class="demand-nav-markers">
        {questions}
        {assignee === null ? null : <AssigneeMarker name={assignee} />}
      </span>
    );
  const spoken = [
    demand.title,
    demand.openQuestionCount > 0 ? questionText : null,
    assignee === null ? null : `assigned to ${assignee}`,
  ]
    .filter((part) => part !== null)
    .join(', ');
  const cancelled = demand.status === 'cancelled';
  return (
    <div
      class="demand-sidebar-row"
      data-key={demand.id}
      data-context-demand={demand.id}
      data-context-cancelled={cancelled ? '' : undefined}
    >
      <ListItem
        label={demand.title}
        icon={<LucideIcon icon={FileText} name="" />}
        trailing={trailing}
        accessibleLabel={spoken}
        action="open-demand-nav"
        itemId={demand.id}
        selected={demand.id === activeId}
        multiline={true}
      />
    </div>
  );
}
